import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AVATARS, GRID_SIZE, MAX_EP, START_EP, MONEY_VAL,
  BURGER_EP, APPLE_EP, BASE_EP_COST, LEVEL_UP_SECONDS, SOUNDS
} from '../constants';
import { buildStartGrid, replenishItems } from '../gameLogic';

let effectId = 0;

// Format seconds as mm:ss
function formatTime(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function GamePage({ profile, onBack, onGameOver, soundSettings, gameDuration }) {
  const av = AVATARS.find(a => a.id === profile?.avatarId) || AVATARS[0];

  const START_R   = Math.floor(GRID_SIZE / 2);
  const START_C   = Math.floor(GRID_SIZE / 2);
  const totalSecs = (gameDuration || 5) * 60;  // convert minutes to seconds

  const [playerPos,    setPlayerPos]    = useState({ r: START_R, c: START_C });
  const [grid,         setGrid]         = useState(() => buildStartGrid(START_R, START_C));
  const [ep,           setEp]           = useState(START_EP);
  const [score,        setScore]        = useState(0);
  const [level,        setLevel]        = useState(1);
  const [timeLeft,     setTimeLeft]     = useState(totalSecs);  // countdown
  const [elapsed,      setElapsed]      = useState(0);          // for score saving
  const [gameOver,     setGameOver]     = useState(false);
  const [goReason,     setGoReason]     = useState('');
  const [effects,      setEffects]      = useState([]);

  const posRef      = useRef({ r: START_R, c: START_C });
  const gridRef     = useRef(grid);
  const epRef       = useRef(START_EP);
  const scoreRef    = useRef(0);
  const levelRef    = useRef(1);
  const elapsedRef  = useRef(0);
  const gameOverRef = useRef(false);
  const bgMusicRef  = useRef(null);
  const gameOverSfxRef = useRef(null);

  gridRef.current  = grid;
  epRef.current    = ep;
  scoreRef.current = score;
  levelRef.current = level;

  // ── Audio ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const bgAudio = new Audio(SOUNDS.bgMusic);
    bgAudio.loop = true; bgAudio.volume = 0.4;
    bgMusicRef.current = bgAudio;
    const goAudio = new Audio(SOUNDS.gameOver);
    goAudio.volume = 0.8;
    gameOverSfxRef.current = goAudio;
    if (soundSettings?.bgm !== false) bgAudio.play().catch(() => {});
    return () => { bgAudio.pause(); bgAudio.src = ''; goAudio.src = ''; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bgMusicRef.current) return;
    if (soundSettings?.bgm === false) bgMusicRef.current.pause();
    else if (!gameOverRef.current) bgMusicRef.current.play().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundSettings?.bgm]);

  const spawnEffect = (r, c, text) => {
    const id = ++effectId;
    setEffects(e => [...e, { id, r, c, text }]);
    setTimeout(() => setEffects(e => e.filter(x => x.id !== id)), 550);
  };

  const endGame = (reason) => {
    gameOverRef.current = true;
    setGameOver(true);
    setGoReason(reason);
    if (bgMusicRef.current) bgMusicRef.current.pause();
    if (soundSettings?.gameover !== false && gameOverSfxRef.current)
      gameOverSfxRef.current.play().catch(() => {});
  };

  // ── Countdown timer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (gameOverRef.current) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          endGame('⏰ Time\'s Up!');
          return 0;
        }
        return t - 1;
      });
      setElapsed(e => {
        const ne = e + 1;
        elapsedRef.current = ne;
        // Level up every LEVEL_UP_SECONDS
        const newLevel = Math.floor(ne / LEVEL_UP_SECONDS) + 1;
        if (newLevel !== levelRef.current) {
          levelRef.current = newLevel;
          setLevel(newLevel);
          spawnEffect(posRef.current.r, posRef.current.c, `⬆️ LV${newLevel}`);
        }
        return ne;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Move player ───────────────────────────────────────────────────────────
  const movePlayer = useCallback((dr, dc) => {
    if (gameOverRef.current) return;
    if (bgMusicRef.current && bgMusicRef.current.paused && soundSettings?.bgm !== false)
      bgMusicRef.current.play().catch(() => {});

    const { r, c } = posRef.current;
    const nr = (r + dr + GRID_SIZE) % GRID_SIZE;
    const nc = (c + dc + GRID_SIZE) % GRID_SIZE;
    posRef.current = { r: nr, c: nc };
    setPlayerPos({ r: nr, c: nc });

    const epCost  = BASE_EP_COST + (levelRef.current - 1) * 0.5;
    let newEp     = epRef.current - epCost;
    let newScore  = scoreRef.current;
    const cell    = gridRef.current[nr][nc];
    let newGrid   = gridRef.current.map(row => [...row]);
    let collected = false;

    if (cell === '💰') {
      newScore += MONEY_VAL; newGrid[nr][nc] = null;
      spawnEffect(nr, nc, '+50 💰'); collected = true;
    } else if (cell === '🍔') {
      newEp = newEp + BURGER_EP; newGrid[nr][nc] = null;
      spawnEffect(nr, nc, '+35 ⚡'); collected = true;
    } else if (cell === '🍎') {
      newEp = newEp + APPLE_EP; newGrid[nr][nc] = null;
      spawnEffect(nr, nc, '+20 ⚡'); collected = true;
    }

    if (collected) newGrid = replenishItems(newGrid, nr, nc);
    setGrid(newGrid);
    scoreRef.current = newScore;
    setScore(newScore);

    if (newEp <= 0)         { setEp(0);      epRef.current = 0;      endGame('💀 Energy Depleted!'); }
    else if (newEp > MAX_EP){ setEp(MAX_EP); epRef.current = MAX_EP; endGame('⚡ Energy Overloaded!'); }
    else                    { setEp(newEp);  epRef.current = newEp; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundSettings]);

  // ── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const KEY_MAP = {
      ArrowUp:[-1,0], ArrowDown:[1,0], ArrowLeft:[0,-1], ArrowRight:[0,1],
      w:[-1,0], s:[1,0], a:[0,-1], d:[0,1],
      W:[-1,0], S:[1,0], A:[0,-1], D:[0,1],
    };
    const handler = e => { if (KEY_MAP[e.key]) { e.preventDefault(); movePlayer(...KEY_MAP[e.key]); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [movePlayer]);

  // ── Restart ───────────────────────────────────────────────────────────────
  const restart = () => {
    const r = START_R, c = START_C;
    posRef.current = {r,c}; epRef.current = START_EP;
    scoreRef.current = 0; levelRef.current = 1;
    elapsedRef.current = 0; gameOverRef.current = false;
    setPlayerPos({r,c}); setEp(START_EP); setScore(0);
    setLevel(1); setTimeLeft(totalSecs); setElapsed(0);
    setGameOver(false); setGoReason(''); setEffects([]);
    setGrid(buildStartGrid(r, c));
    if (bgMusicRef.current && soundSettings?.bgm !== false) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.play().catch(() => {});
    }
  };

  const epPct   = (ep / MAX_EP) * 100;
  const epColor = epPct < 20 ? '#FF4060' : epPct < 50 ? '#FF6B35' : epPct > 85 ? '#FF6B35' : '#6BCB77';

  // Timer color — goes red when under 30 seconds
  const timerColor = timeLeft <= 30 ? '#FF4060' : timeLeft <= 60 ? '#FF6B35' : '#4D96FF';

  return (
    <div className="game-page">

      {/* HUD */}
      <div className="game-hud">
        <div className="hud-card">
          <span className="hud-icon">🏆</span>
          <div><span className="hud-label">Score</span><span className="hud-value">{score}</span></div>
        </div>
        <div className="hud-card">
          <span className="hud-icon">⏱️</span>
          <div>
            <span className="hud-label">Time Left</span>
            <span className="hud-value" style={{ color: timerColor }}>{formatTime(timeLeft)}</span>
          </div>
        </div>
        <div className="hud-card">
          <span className="hud-icon">⚡</span>
          <div>
            <span className="hud-label">Energy</span>
            <span className="hud-value" style={{ color: epColor }}>{Math.round(ep)}</span>
          </div>
        </div>
        <div className="hud-card">
          <div className="level-badge">LV {level}</div>
        </div>
      </div>

      {/* Energy bar */}
      <div className="energy-bar-wrap">
        <div className="energy-label-row">
          <span>⚡ Energy</span>
          <span style={{ color: epColor }}>{Math.round(ep)} / {MAX_EP}</span>
        </div>
        <div className="energy-bar-bg">
          <div className="energy-bar-fill" style={{ width: `${epPct}%`, background: epColor }} />
        </div>
      </div>

      {/* 8x8 Grid */}
      <div className="grid-wrapper">
        <div className="game-grid">
          {Array.from({ length: GRID_SIZE }, (_, r) =>
            Array.from({ length: GRID_SIZE }, (_, c) => {
              const isPlayer = playerPos.r === r && playerPos.c === c;
              const cell     = grid[r][c];
              const fx       = effects.find(e => e.r === r && e.c === c);
              return (
                <div key={`${r}-${c}`} className={[
                  'grid-cell',
                  isPlayer ? 'has-player' : '',
                  !isPlayer && cell === '💰' ? 'has-money' : '',
                  !isPlayer && (cell === '🍔' || cell === '🍎') ? 'has-food' : '',
                ].join(' ')}>
                  {isPlayer ? av.emoji : (cell || null)}
                  {fx && <div className="collect-effect">{fx.text}</div>}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Mobile D-Pad */}
      <div className="mobile-controls">
        <button className="ctrl-btn ctrl-up"    onPointerDown={() => movePlayer(-1,  0)}>▲</button>
        <button className="ctrl-btn ctrl-left"  onPointerDown={() => movePlayer( 0, -1)}>◀</button>
        <div    className="ctrl-center">{av.emoji}</div>
        <button className="ctrl-btn ctrl-right" onPointerDown={() => movePlayer( 0,  1)}>▶</button>
        <button className="ctrl-btn ctrl-down"  onPointerDown={() => movePlayer( 1,  0)}>▼</button>
      </div>

      {/* Legend */}
      <div style={{ display:'flex', gap:'16px', justifyContent:'center', padding:'6px 16px',
        fontSize:'0.85rem', color:'#888', flexWrap:'wrap' }}>
        <span>🍔 +35⚡</span><span>🍎 +20⚡</span><span>💰 +50pts</span><span>⚡ Max: 350</span>
      </div>

      <div style={{ padding: '10px', textAlign: 'center' }}>
        <button className="btn-secondary" style={{ padding: '10px 24px', fontSize: '0.9rem' }} onClick={onBack}>
          🏠 Home
        </button>
      </div>

      {/* GAME OVER */}
      {gameOver && (
        <div className="game-over-overlay">
          <div className="game-over-box">
            <span className="game-over-emoji">{goReason.includes('⏰') ? '⏰' : '💀'}</span>
            <div className="game-over-title">
              {goReason.includes('⏰') ? "Time's Up!" : 'Game Over!'}
            </div>
            <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '4px' }}>{goReason}</div>
            <div className="game-over-label">Final Score</div>
            <div className="game-over-score">{score}</div>
            <div className="game-over-stats">
              <div className="stat-card">
                <div className="stat-value">{formatTime(elapsed)}</div>
                <div className="stat-label">Survived</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{level}</div>
                <div className="stat-label">Max Level</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{(score / Math.max(elapsed,1)).toFixed(1)}</div>
                <div className="stat-label">Pts / Sec</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{Math.floor(score / MONEY_VAL)}</div>
                <div className="stat-label">Coins</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
              <button className="btn-primary" style={{ fontSize:'1rem', padding:'12px 22px' }}
                onClick={() => onGameOver(score, elapsed)}>
                🏠 Save & Home
              </button>
              <button className="btn-secondary" style={{ fontSize:'0.9rem', padding:'12px 22px' }}
                onClick={restart}>
                🔄 Play Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}